import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import { DateTime } from "luxon"
import { FaPlus } from "react-icons/fa"
import { FiSettings, FiAlertTriangle } from "react-icons/fi"

import useT from "utils/hooks/useT"
import { ConfigContainer, Popup } from "components"
import { Container, Title, Text, Select } from "components/Form"
import { limsConsumables, message } from "state_management"
import {
  CONSUMABLE_TYPES,
  CONSUMABLE_TYPE_FIELDS,
  consumableType,
  validityStatus,
  standardStatus,
  isStandardExpiringSoon,
  standardLifecycleLabelKey,
} from "state_management/lims/limsConsumables"
import ESignatureModal from "components/Lims/ESignatureModal"

// Consumables register (F-B3, PRD-003 Area 3). One register for every kind of consumable a QC
// run uses — reagent, reference standard, column and volumetric glassware — selected via a
// type-tab strip. The table columns and the add/edit form are driven by CONSUMABLE_TYPE_FIELDS
// for the active type, so each tab shows its own fields and validity. Any LIMS user may add /
// edit / delete (lab inventory, not an authorisation). Expired / overdue / used-up rows are
// flagged. The data is consumed later by result entry (F-C2) for usage traceability.
export default function ConsumablesRegister() {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const consumables = useSelector(limsConsumables.selectConsumables)

  const [activeType, setActiveType] = useState(CONSUMABLE_TYPES[0])
  // null = closed; {} = creating; a consumable object = editing that record.
  const [editing, setEditing] = useState(null)
  // A pending inline reference-standard status change awaiting an e-signed reason, or null:
  // { consumable, nextStatus }. Reuses ESignatureModal in reason-only mode (lab inventory —
  // no capability gate); the change is applied on sign (SPEC-015).
  const [statusChange, setStatusChange] = useState(null)

  useEffect(() => {
    dispatch(limsConsumables.fetch())
  }, [dispatch])

  const handleClose = () => setEditing(null)

  const descriptor = CONSUMABLE_TYPE_FIELDS[activeType]
  const isStandardTab = activeType === "standard"
  const visibleConsumables = consumables.filter((c) => consumableType(c) === activeType)

  // The analyst signed the inline status change (reason-only): persist the new lifecycle status
  // — the reason rides top-level so the server writes the limsAuditStandardStatusChanged audit
  // entry with it (SPEC-021 m3). Returns the promise so the dialog surfaces a failure inline.
  const handleStatusChangeSign = (signature) => {
    const { consumable, nextStatus } = statusChange
    const reason = (signature && signature.reason) || ""
    return dispatch(limsConsumables.setStandardStatus(consumable.id, nextStatus, reason)).then(() => {
      dispatch(message.success(t("limsStandardStatusChanged", { name: consumable.name })))
      setStatusChange(null)
    })
  }

  const handleSave = (record) => {
    // Persist name + type + only the fields the active type defines (others stay empty).
    const payload = { name: record.name, type: activeType }
    descriptor.fields.forEach(({ key }) => {
      payload[key] = record[key] !== undefined ? record[key] : ""
    })
    const isCreating = !record.id
    const verb = isCreating
      ? dispatch(limsConsumables.create(payload))
      : dispatch(limsConsumables.edit(record.id, payload))
    verb
      .then(() => {
        dispatch(
          message.success(
            t(isCreating ? "limsReagentCreated" : "limsReagentUpdated", { name: record.name })
          )
        )
        setEditing(null)
      })
      .catch(() => {
        dispatch(message.error(t("limsReagentSaveFailed", { name: record.name })))
      })
  }

  const handleDelete = (record) => {
    dispatch(limsConsumables.remove(record.id, record.name))
      .then(() => {
        dispatch(message.success(t("limsReagentDeleted", { name: record.name })))
        setEditing(null)
      })
      .catch(() => {
        dispatch(message.error(t("limsReagentDeleteFailed", { name: record.name })))
      })
  }

  return (
    <div className="LimsConsumables">
      <div className="LimsConsumables-header">
        <h1 className="LimsConsumables-title">{t("limsConsumables")}</h1>
        <Button variant="primary" onClick={() => setEditing({})}>
          <FaPlus /> {t("limsConsumableAdd")}
        </Button>
      </div>

      <div className="LimsConsumables-tabs">
        {CONSUMABLE_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            className={"LimsConsumables-tab" + (type === activeType ? " active" : "")}
            onClick={() => setActiveType(type)}
          >
            {t(CONSUMABLE_TYPE_FIELDS[type].labelKey)}
          </button>
        ))}
      </div>

      {visibleConsumables.length === 0 ? (
        <p className="LimsConsumables-empty">{t("limsConsumablesNoEntries")}</p>
      ) : (
        <Table striped hover responsive className="LimsConsumables-table">
          <thead>
            <tr>
              <th>{t("limsReagentName")}</th>
              {descriptor.fields.map((field) => (
                <th key={field.key}>{t(field.labelKey)}</th>
              ))}
              <th>{t("limsReagentStatus")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleConsumables.map((consumable) => {
              // The reference-standard tab shows the richer derived standardStatus (approved /
              // quarantine / expired / discarded) with its own colour scale + an "expiring soon"
              // badge; other types keep the simple validityStatus (active / expired / overdue).
              if (isStandardTab) {
                const stdStatus = standardStatus(consumable)
                const expiringSoon =
                  isStandardExpiringSoon(consumable) && (stdStatus === "approved" || stdStatus === "quarantine")
                return (
                  <tr
                    key={consumable.id}
                    className={
                      stdStatus === "expired" || stdStatus === "discarded"
                        ? "LimsConsumables-row--expired"
                        : undefined
                    }
                  >
                    <td>{consumable.name}</td>
                    {descriptor.fields.map((field) => (
                      <td key={field.key}>{formatFieldValue(field, consumable[field.key], t)}</td>
                    ))}
                    <td>
                      <span className={"LimsConsumables-status " + standardStatusClass(stdStatus)}>
                        {t(standardStatusLabelKey(stdStatus))}
                      </span>
                      {expiringSoon && (
                        <span className="LimsConsumables-badge--expiring" title={t("limsStandardExpiringSoon")}>
                          <FiAlertTriangle /> {t("limsStandardExpiringSoon")}
                        </span>
                      )}
                    </td>
                    <td className="LimsConsumables-actions">
                      {/* Inline lifecycle-status change (quarantine / approved / discarded) — a
                          GMP decision that requires a stated reason -> audit (SPEC-015). */}
                      <Select
                        isMulti={false}
                        collection={STANDARD_STATUS_OPTIONS.map((s) => ({ id: s, name: t(standardLifecycleLabelKey(s)) }))}
                        value={consumable.lifecycleStatus || "approved"}
                        onChange={(v) => {
                          const next = v[0]
                          if (next && next !== (consumable.lifecycleStatus || "approved")) {
                            setStatusChange({ consumable, nextStatus: next })
                          }
                        }}
                        menuPlacement="bottom"
                      />
                      <FiSettings
                        className="LimsConsumables-edit-icon"
                        title={t("edit")}
                        onClick={() => setEditing(consumable)}
                      />
                    </td>
                  </tr>
                )
              }
              const status = validityStatus(consumable)
              const invalid = status !== "active"
              return (
                <tr
                  key={consumable.id}
                  className={invalid ? "LimsConsumables-row--expired" : undefined}
                >
                  <td>{consumable.name}</td>
                  {descriptor.fields.map((field) => (
                    <td key={field.key}>{formatFieldValue(field, consumable[field.key], t)}</td>
                  ))}
                  <td>
                    <span className={"LimsConsumables-status " + statusClass(status)}>
                      {t(statusLabelKey(status))}
                    </span>
                  </td>
                  <td>
                    <FiSettings
                      className="LimsConsumables-edit-icon"
                      title={t("edit")}
                      onClick={() => setEditing(consumable)}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}

      {editing && (
        <ConsumableConfig
          consumable={editing.id ? editing : null}
          type={activeType}
          descriptor={descriptor}
          onClose={handleClose}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      )}

      {statusChange && (
        <ESignatureModal
          title={t("limsStandardStatusChangeTitle", { name: statusChange.consumable.name })}
          onSign={handleStatusChangeSign}
          onCancel={() => setStatusChange(null)}
        />
      )}
    </div>
  )
}

// The reference-standard lifecycle statuses offered in the inline status-change dropdown.
const STANDARD_STATUS_OPTIONS = ["approved", "quarantine", "discarded"]

// Derived reference-standard status -> i18n key (SPEC-015).
function standardStatusLabelKey(status) {
  if (status === "approved") return "limsStandardStatusApproved"
  if (status === "quarantine") return "limsStandardStatusQuarantine"
  if (status === "expired") return "limsStandardStatusExpired"
  if (status === "discarded") return "limsStandardStatusDiscarded"
  return "limsStandardStatusApproved"
}

// Status -> badge colour class: approved = green, quarantine = yellow, expired/discarded = red.
function standardStatusClass(status) {
  if (status === "approved") return "is-active"
  if (status === "quarantine") return "is-quarantine"
  return "is-expired"
}

// "active" -> Active; "expired" -> Expired; "overdue" -> Overdue.
function statusLabelKey(status) {
  if (status === "expired") return "limsReagentExpired"
  if (status === "overdue") return "limsConsumableOverdue"
  return "limsReagentActive"
}

function statusClass(status) {
  return status === "active" ? "is-active" : "is-expired"
}

function formatFieldValue(field, value, t) {
  if (value === undefined || value === null || value === "") return ""
  if (field.kind === "date") {
    const dt = DateTime.fromISO(value)
    return dt.isValid ? dt.toFormat("yyyy-LL-dd") : value
  }
  if (field.kind === "select" && field.optionLabelKey) {
    const key = field.optionLabelKey(value)
    return key ? t(key) : String(value)
  }
  return String(value)
}

function fieldInputType(kind) {
  if (kind === "date") return "date"
  if (kind === "number") return "number"
  return "text"
}

// Add / edit form for a single consumable, rendered in a Popup with a ConfigContainer. The
// fields shown are driven by the per-type descriptor (always a Name field, then the type's
// fields). Editable by any LIMS user.
function ConsumableConfig({ consumable, type, descriptor, onClose, onSave, onDelete }) {
  const { t } = useT("common")
  const isCreating = !consumable
  const [record, setRecord] = useState(() => {
    if (consumable) return { ...consumable }
    const seed = { name: "", type }
    descriptor.fields.forEach(({ key }) => {
      seed[key] = ""
    })
    return seed
  })

  const handleChange = (field, value) => {
    setRecord((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = () => {
    onSave(record)
    return true
  }

  return (
    <Popup nonScrollable onCancel={onClose}>
      <ConfigContainer
        title={isCreating ? t("limsConsumableAdd") : t("limsConsumableEdit", { name: consumable.name })}
        cancelHandler={onClose}
        submitHandler={handleSubmit}
        deleteHandler={isCreating ? null : () => onDelete(consumable)}
        deleteIsImportant
        confirmDeletionText={t("limsReagentConfirmDelete", { name: record.name })}
      >
        <Container className="LimsConsumables-field">
          <Title>
            {t("limsReagentName")} <div className="red-text">*</div>
          </Title>
          <Text
            value={record.name}
            required
            onChange={(value) => handleChange("name", value)}
            placeholder={t("limsReagentNamePlaceholder")}
          />
        </Container>

        {descriptor.fields.map((field) => (
          <Container className="LimsConsumables-field" key={field.key}>
            <Title>
              {t(field.labelKey)} {field.required && <div className="red-text">*</div>}
            </Title>
            {field.kind === "select" ? (
              <Select
                isMulti={false}
                includeNone
                collection={field.options.map((opt) => ({ id: opt, name: t(field.optionLabelKey(opt)) }))}
                value={record[field.key] || ""}
                onChange={(value) => handleChange(field.key, value[0] || "")}
                menuPlacement="bottom"
              />
            ) : (
              <Text
                type={fieldInputType(field.kind)}
                value={record[field.key] === undefined || record[field.key] === null ? "" : record[field.key]}
                required={field.required}
                onChange={(value) => handleChange(field.key, value)}
                placeholder={fieldPlaceholder(field, t)}
              />
            )}
          </Container>
        ))}
      </ConfigContainer>
    </Popup>
  )
}

function fieldPlaceholder(field, t) {
  if (field.placeholderKey) return t(field.placeholderKey)
  if (field.kind === "date") return t("dateFormatYyyyMmDd")
  return ""
}
